import {
  containsLowerCase,
  containsNumbers,
  containsUpperCase,
} from "@utils/regexValidators";
import { create } from "zustand";
import {
  FormDataKey,
  UseFormDataState,
  formData,
  DataForSubmissionType,
} from "./formData";
import { QuestionAttribute } from "@components/BuilderComponents/SignupFormDrip/sfd-components/Form/sfd-helpers/sfd-form.type";

type UseFormDataActions = {
  getDataForSubmission: () => DataForSubmissionType;
  setAgreementValue: (key: FormDataKey, checked: boolean) => void;
  setError: (key: FormDataKey, hasError: boolean) => void;
  setIsValid: (key: FormDataKey, isValid: boolean) => void;
  setPasswordValidators: (value: string) => void;
  getQuestionValue: (name?: string) => QuestionAttribute | null;
  setQuestions: (key: string, value: string[]) => void;
  setQuestionError: (name: string, value: boolean) => void;
  setQuestionValid: (name: string, value: boolean) => void;
  setValue: (key: FormDataKey, value: string) => void;
  setInitialQuestionValues: (value?: {
    [key: string]: QuestionAttribute;
  }) => void;
  questions: {
    [key: string]: QuestionAttribute;
  };
  setOfferPromoCode: (promoCode?: string | null) => void;
};

type UseFormDataType = UseFormDataState &
  UseFormDataActions & { offerPromoCode?: string | null };

/**
   * The ``useFormData`` store is used to handle sign up form fields.
   *
   * All fields are are going to have a similar structure.
   *
   * ``e.g``
   * ```ts
   * ...
   * firstName: {
      value: "field value",
      hasError: false,
      isValid: false,
    },
    ...
  * ```
  * the only field that have a different structure is the ``agreement`` field,
  *
  * and this is because the input is a check box.
  *
  * ``e.g``
  * ```ts
  * ...
  * agreement: {
      checked: false,
      hasError: false,
      isValid: false,
    },
    ...
  * ```
  * So Far we only have 5 methods
  * - ``setError`` -> handles hasError property for fields
  * - ``setIsValid`` -> handles isValid property for fields
  * - ``setValue`` -> handles value property for fields
  * - ``setAgreementValue`` -> checked value property for agreement field
  * - ``getDataForSubmission`` -> returns formated data to be submited to the web service.
  *
  * ``e.g``
  * ```ts
  * const data = {
      email: "",
      firstName: "",
      globalProfessionId: "",
      lastName: "",
      otherProfession: "",
      password: "",
      phoneNumber: "",
      termsOfServiceAcceptance: "",
    };
  * ```
 */
export const useFormData = create<UseFormDataType>((set, get) => ({
  ...formData,
  setError: (key, hasError) => {
    set(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        hasError,
      },
    }));
  },
  setIsValid: (key, isValid) => {
    set(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        isValid,
      },
    }));
  },
  setValue: (key, value) => {
    set(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
      },
    }));
  },
  setInitialQuestionValues: questions => {
    set(prev => ({
      ...prev,
      questions,
    }));
  },
  getQuestionValue: (name?: string) => {
    const { questions } = get();

    return questions?.[name || ""];
  },
  setQuestions: (key: string, value: string[]) => {
    set(prev => ({
      ...prev,
      questions: {
        ...prev.questions,
        [key]: {
          ...prev.questions[key],
          value,
          isValid: true,
          hasError: false,
        },
      },
    }));
  },
  setQuestionError: (key: string, hasError: boolean) => {
    set(prev => ({
      ...prev,
      questions: {
        ...prev.questions,
        [key]: {
          ...prev.questions[key],
          hasError,
        },
      },
    }));
  },
  setQuestionValid: (key: string, isValid: boolean) => {
    set(prev => ({
      ...prev,
      questions: {
        ...prev.questions,
        [key]: {
          ...prev.questions[key],
          isValid,
        },
      },
    }));
  },
  setAgreementValue: (key, checked) => {
    set(prev => {
      const temp = {
        ...prev,
        [key]: {
          ...prev[key],
          checked,
        },
      };

      return temp;
    });
  },
  getDataForSubmission: () => {
    const {
      agreement,
      email,
      firstName,
      lastName,
      otherProfession,
      password,
      phone,
      profession,
      questions,
    } = get();

    return {
      email: email?.value,
      firstName: firstName?.value,
      globalProfessionId: profession.value,
      lastName: lastName?.value,
      otherProfession: otherProfession.value,
      password: password.value,
      phoneNumber: phone.value,
      termsOfServiceAcceptance: agreement.checked,
      questions,
    };
  },
  setPasswordValidators: value => {
    const { password } = get();

    const temp = {
      ...password,
      validators: {
        ...password.validators,
        charactersMin: value.length >= 8,
        lowerLetter: containsLowerCase(value),
        oneNumber: containsNumbers(value),
        uppercaseLetter: containsUpperCase(value),
      },
    };

    set(state => ({
      ...state,
      password: temp,
    }));
  },
  setOfferPromoCode: offerPromoCode => {
    set({ offerPromoCode });
  },
}));
